import * as React from 'react';
import { Query } from 'react-apollo';
import gql from 'graphql-tag';
import glamorous from 'glamorous';
import { RvLoader } from '../../components/Loader';
import { SearchCriteria } from '../SearchCriteria';
import CloseIcon from '@material-ui/icons/Close';
import { ChartCommonProps, IconButtonWrapper, ChartCommonState, HelpWrapper } from '../Common';
import { WithStyles, withStyles, IconButton } from '@material-ui/core';
import { styles } from '../styles';
import { ChartFilter } from '../ChartComponents';
import { convertStringNumberToNumber, formatNumber, getMaxTickLimit, getSuggestedMaxTick } from '../../helpers/ChartHelper';
import { SelectedItemProps } from '../MattersByStatusChart';
import { DrillDownType } from '../DrillDownType';
import { Bar, getElementAtEvent } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
    Scale,
    Tick,
    Chart,
    ChartEvent,
    ActiveElement,
} from 'chart.js';
import zoomPlugin from 'chartjs-plugin-zoom';
import { ChartInfo } from '../../components/ChartInfo';

import { RV_CHART_CLASS, RV_CHART_COLORS, isChartDataEmpty, rvBarDatasetStyle, rvChartAnimation, rvCategoryAxisStyle, rvTooltipStyle } from '../chartTheme';
import { ChartHeading } from '../ChartHeading';
import { ChartStatTiles } from '../ChartStatTiles';
import { AnimatedNumber } from '../AnimatedNumber';
import { ChartSkeleton } from '../../components/loading/ChartSkeleton';
import { ChartEmptyState } from '../ChartEmptyState';
import ReceiptOutlinedIcon from '@material-ui/icons/ReceiptOutlined';
ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
    zoomPlugin
);

const BarWrapper = glamorous.div<{ theme?: object }>((props) => ({
    position: 'relative',
    paddingBottom: '10px',
    flex: 1,
    display: 'flex',
    flexFlow: 'column',
    maxWidth: '100%',
    maxHeight: '100%',
    height: '100%',
    '@media print': {
        display: 'flex',
        flexFlow: 'column',
        alignItems: 'center',
    }
}));

const BarTitleContainer = glamorous.div<{ theme?: object }>((props) => ({
    display: 'flex',
    flexFlow: 'column',
    alignItems: 'center',
    padding: '15px 15px 0px 15px',
    fontSize: '16px',
    fontWeight: 600,
    [`& .${`h1`}`]: {
        display: 'flex',
        alignItems: 'center',
    },
    [`& .${`h2`}`]: {
        fontSize: '2.5rem',
        padding: '10px 0',
    },
})).withProps({ className: 'rv-chart-title-area' });

const BarContainer = glamorous.div<{ theme?: object }>((props) => ({
    padding: '5px 15px 15px 15px',
    minHeight: '300px',
    flex: 1,
    display: 'flex',
    flexFlow: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    maxHeight: '350px',
    maxWidth: 'auto',
    position: 'relative',
    '@media (max-width: 533px)': {
        maxWidth: '350px',
        margin: '0 auto',
    },
    [`& .${`titleWrapper`}`]: {
        textAlign: 'center',
        display: 'inline-block',
        width: '100%',
        padding: '15px',
        [`& .${`title`}`]: {
            fontSize: '18px',
            fontWeight: 600,
            display: 'block',
            marginBottom: '15px',
        },
        [`& .${`value`}`]: {
            fontSize: '24px',
            fontWeight: 600,
            display: 'block',
            marginBottom: '15px',
        },
        [`& .${`secondary-title`}`]: {
            fontSize: '16px',
            fontWeight: 400,
            display: 'block',
            marginBottom: '15px',
        },
    }
}));

const LoadWrapper = glamorous.div<{ theme?: object }>((props) => ({
    padding: '20px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100%',
}));

const TotalDiv = glamorous.div<{ theme?: object }>((props) => ({
    cursor: 'pointer',
    paddingRight: 5
}));

interface ChartProps extends SelectedItemProps, ChartCommonProps, WithStyles<typeof styles> {
    chartFilter?: ChartFilter;
}

interface ChartState extends ChartCommonState {
    // 
}

class PipelineVsMatterActivityChart extends React.Component<ChartProps, ChartState>  {

    // tslint:disable-next-line: no-any
    public chartRef: React.RefObject<any>;

    constructor(props: ChartProps, state: ChartState) {
        super(props, state);
        this.onCloseChart = this.onCloseChart.bind(this);
        this.handlePopoverOpen = this.handlePopoverOpen.bind(this);
        this.handlePopoverClose = this.handlePopoverClose.bind(this);
        this.handleTotalClick = this.handleTotalClick.bind(this);
        this.onHover =  this.onHover.bind(this);

        this.onPan = this.onPan.bind(this);

        this.chartRef = React.createRef();

        this.state = {
            open: false,
            anchorEl: undefined
        };
    }

    public componentDidMount() {
        // 
    }

    public componentWillUnmount() {
        // 
    }

    public shouldComponentUpdate(nextProps: ChartProps, nextState: ChartState): boolean {
        if (this.props !== nextProps || this.state !== nextState) {
           return true;
        }

        return false;
    }

    public render() {

        return (
            <React.Fragment>
                {/* tslint:disable-next-line: no-any */}
                <Query<any> 
                    query={ChartData}
                    variables={{
                        entityType: this.props.chartFilter?.entityType,
                        entityGuid: this.props.chartFilter?.entityGuid,
                        responsibility: this.props.chartFilter?.responsibility
                    }}
                >
                    {({ loading, error, data }) => {

                        if (loading) {
                            return (
                                <ChartSkeleton variant="columns" />
                            );
                        }

                        const statistics = data.statistics;

                        let matter = (loading || error ||
                            statistics === null || statistics === undefined)
                            ? null
                            : statistics.matter;

                        let pipelineVsMatterActivity = matter ?
                            matter.pipelineVsMatterActivity : null;

                        if (pipelineVsMatterActivity) {

                            const { lessThan30, from31To60, from61To90, greaterThan90 } = pipelineVsMatterActivity.matterActivity;
                            const { lessThan30Fees, from31To60Fees, from61To90Fees, greaterThan90Fees } = pipelineVsMatterActivity.matterPipeline;
                            let totalFees = pipelineVsMatterActivity.currentMatterEstimateDetails.totalFees;
                            let mattersCount = pipelineVsMatterActivity.currentMatterEstimateDetails.mattersCount;
                            let noEstimate = pipelineVsMatterActivity.currentMatterEstimateDetails.noEstimate;
                            let withinEstimate = pipelineVsMatterActivity.currentMatterEstimateDetails.withinEstimate;
                            let exceedingEstimate = pipelineVsMatterActivity.currentMatterEstimateDetails.exceedingEstimate;

                            return (
                                <BarWrapper className={RV_CHART_CLASS}>
                                    <IconButtonWrapper
                                        aria-label="close"
                                    >
                                        <HelpWrapper className="rv-chart-help">
                                            <ChartInfo 
                                                description={this.props.description}
                                            />
                                        </HelpWrapper>
                                        <IconButton
                                            aria-label="close"
                                            onClick={this.onCloseChart}
                                            size="small"
                                        >
                                            <CloseIcon />
                                        </IconButton>
                                    </IconButtonWrapper>
                                    {/* tslint:disable-next-line: no-any */}
                                    <Query<any> query={CurrencySymbol}>
                                        {({
                                            // tslint:disable-next-line: no-shadowed-variable
                                            loading, error, data
                                        }) => {

                                            if (loading) {
                                                return (
                                                    <LoadWrapper className="load-wrapper">
                                                        <RvLoader size="small" />
                                                    </LoadWrapper>
                                                );
                                            }

                                            const currencySymbol = (loading || error) ?
                                               null : data.options.currencySymbol;

                                            const symbol = currencySymbol ? currencySymbol.symbol : null;

                                            return (
                                                <BarTitleContainer>
                                                    <ChartHeading icon={<ReceiptOutlinedIcon />} title="Expected Future Fees" />
                                                    <TotalDiv className="h2" onClick={() => this.handleTotalClick()}>
                                                        <AnimatedNumber value={totalFees} format={(amount) => `${symbol}${formatNumber(amount)}`} />
                                                    </TotalDiv>
                                                    <div className="h3">
                                                        <TotalDiv onClick={() => this.handleTotalClick()}>
                                                            {formatNumber(withinEstimate)}
                                                        </TotalDiv>                                                      
                                                        current matters still within estimate
                                                    </div>
                                                    <div className="h3">Expected Future Fees vs Matter Activity</div>
                                                </BarTitleContainer>
                                            );
                                        }}
                                    </Query>
                                    <BarContainer className="rv-chart-bars">
                                        {/* tslint:disable-next-line: no-any */}
                                        <Query<any> query={CurrencySymbol}>
                                            {({
                                                // tslint:disable-next-line: no-shadowed-variable
                                                loading, error, data
                                            }) => {

                                                if (loading) {
                                                    return (
                                                        <LoadWrapper className="load-wrapper">
                                                            <RvLoader size="small" />
                                                        </LoadWrapper>
                                                    );
                                                }

                                                const currencySymbol = (loading || error) ?
                                                    null : data.options.currencySymbol;

                                                const symbol = currencySymbol ? currencySymbol.symbol : null;
                                                return (
                                                    <>
                                                        <Bar
                                                            ref={this.chartRef}
                                                            // tslint:disable-next-line: no-any
                                                            onClick={(event: React.MouseEvent<any>) => {
                                                                const _data = getElementAtEvent(this.chartRef.current, event);
                                                                if (_data.length > 0 && this.props.getSelectedItem) {
            
                                                                    const _index = getElementAtEvent(this.chartRef.current, event)[0].index;
            
                                                                    const item = ['Last 30 Days', '31-60 Days ago', '61-90 Days ago', 'Greater Than 90 days'][_index];

                                                                    const searchCriterias = new Array<SearchCriteria>();

                                                                    if (item === 'Last 30 Days') {
                                                                        searchCriterias.push(
                                                                            {property: 'LastUpdateEndByPeriod', operator: 'gteq', comparison: '30'}
                                                                        );
                                                                    } else if (item === '31-60 Days ago') {
                                                                        searchCriterias.push(
                                                                            { property: 'LastUpdateStartByPeriod', operator: 'lt', comparison: '30' },
                                                                            { property: 'LastUpdateEndByPeriod', operator: 'gteq', comparison: '60' }
                                                                        );
                                                                    } else if (item === '61-90 Days ago') {
                                                                        searchCriterias.push(
                                                                            { property: 'LastUpdateStartByPeriod', operator: 'lt', comparison: '60' },
                                                                            { property: 'LastUpdateEndByPeriod', operator: 'gteq', comparison: '90' }
                                                                        );
                                                                    } else if (item === 'Greater Than 90 days') {
                                                                        searchCriterias.push(
                                                                            { property: 'LastUpdateStartByPeriod', operator: 'lt', comparison: '90' }
                                                                        );
                                                                    }

                                                                    searchCriterias.push(
                                                                        { property: 'InProgress', operator: 'eq', comparison: '1'},
                                                                        {property: 'EstimatedCosts', operator: 'gt', comparison: '0'},
                                                                        {property: 'ExpectedFutureFees', operator: 'gteq', comparison: '0'}
                                                                    );

                                                                    this.props.getSelectedItem(
                                                                        searchCriterias,
                                                                        true,
                                                                        'Matter Activity',
                                                                        DrillDownType.matterSummaryDrilldown,
                                                                        'Matters'
                                                                    );
                                                                }
                                                            }}
                                                            data={{
                                                                labels: ['Last 30 Days', '31-60 Days ago', '61-90 Days ago', 'Greater Than 90 days'],
                                                                datasets: [
    
                                                                    {
                                                                        label: 'Matters',
                                                                        yAxisID: 'yA',
                                                                        fill: false,
                                                                        backgroundColor: RV_CHART_COLORS.amber,
                                                                        borderColor: RV_CHART_COLORS.amber,
                                                                        borderWidth: 3,
                                                                        hoverBackgroundColor: RV_CHART_COLORS.amber,
                                                                        hoverBorderColor: RV_CHART_COLORS.amber,
                                                                        tension: 0.35,
                                                                        pointRadius: 0,
                                                                        data: [lessThan30, from31To60, from61To90, greaterThan90],
                                                                        // tslint:disable-next-line: no-any
                                                                        type: 'line' as any,
                                                                        pointStyle: 'line'
                                                                    },
                                                                    {
                                                                        label: 'Pipeline',
                                                                        yAxisID: 'yB',
                                                                        ...rvBarDatasetStyle(RV_CHART_COLORS.primary),
                                                                        data: [lessThan30Fees, from31To60Fees, from61To90Fees, greaterThan90Fees]
                                                                    }
                                                                // tslint:disable-next-line: no-any
                                                                ] as any
                                                            }}
                                                            options={{
                                                                onHover: this.onHover,
                                                                indexAxis: 'x' as const,
                                                                animation: rvChartAnimation(),
                                                                elements: { bar: { borderWidth: 0 } },
                                                                responsive: true,
                                                                maintainAspectRatio: false,
                                                                scales: {
                                                                  yA: {
                                                                    position: 'left',
                                                                    // beginAtZero: true,
                                                                    suggestedMax: getSuggestedMaxTick([lessThan30, from31To60, from61To90, greaterThan90]),
                                                                    beginAtZero: true,
                                                                    ticks: {
                                                                        maxTicksLimit: getMaxTickLimit([lessThan30, from31To60, from61To90, greaterThan90]),
                                                                        autoSkip: false,
                                                                        callback: function (this: Scale, tickValue: number, index: number, ticks: Tick[]) {
                                                                            return `${symbol}${formatNumber(tickValue)}`;
                                                                        }
                                                                    }
                                                                  },
                                                                  yB: {
                                                                    position: 'right',
                                                                    // beginAtZero: true,
                                                                    suggestedMax: getSuggestedMaxTick([lessThan30, from31To60, from61To90, greaterThan90]),
                                                                    beginAtZero: true,
                                                                    ticks: {
                                                                        maxTicksLimit: getMaxTickLimit([lessThan30, from31To60, from61To90, greaterThan90]),
                                                                        autoSkip: false,
                                                                        callback: function (this: Scale, tickValue: number, index: number, ticks: Tick[]) {
                                                                            return `${symbol}${formatNumber(tickValue)}`;
                                                                        }
                                                                    }
                                                                  },
                                                                  x: {
                                                                    grid: rvCategoryAxisStyle.grid,
                                                                    // min: 0,
                                                                    // max: 9 // minimum display
                                                                    ticks: {
                                                                        ...rvCategoryAxisStyle.ticks,
                                                                        autoSkip: false
                                                                    }
                                                                  }
                                                                },
                                                                plugins: {
                                                                  legend: {
                                                                    position: 'bottom' as const,
                                                                    display: true
                                                                  },
                                                                  title: {
                                                                    display: false,
                                                                    text: ``,
                                                                  },
                                                                  zoom: {
                                                                    pan: {
                                                                      enabled: false,
                                                                      onPan: this.onPan
                                                                    },
                                                                    zoom: {
                                                                      wheel: {
                                                                        enabled: false
                                                                      }
                                                                    }
                                                                    // limits: {
                                                                    //   y: { min: 0, max: 9 },
                                                                    // },
                                                                  },
                                                                  tooltip: {
                                                                    ...rvTooltipStyle,
                                                                    callbacks: {
                                                                        // tslint:disable-next-line: no-any
                                                                        label: function (context: any) {
                                                                            const { 
                                                                                //   dataIndex, 
                                                                                //   label, 
                                                                                formattedValue,
                                                                                // dataset,
                                                                                datasetIndex
                                                                            } = context;
                
                                                                            var currentValue = convertStringNumberToNumber(formattedValue);
                                                                            
                                                                            if (datasetIndex === 1) {
                                                                                return `${symbol}${formatNumber(currentValue)}`;
                                                                            }
    
                                                                            return `${formatNumber(currentValue)}`;
                                                                        },
                                                                    }
                                                                  }
                                                                },
                                                            }}
                                                        />
                                                        {isChartDataEmpty([lessThan30, from31To60, from61To90, greaterThan90]) && (
                                                            <ChartEmptyState title="No data" hint="Nothing to show for this selection." />
                                                        )}
                                                    </>
                                                );
                                            }}
                                        </Query>
                                    </BarContainer>
                                    <ChartStatTiles
                                        caption="Current matters"
                                        tiles={[
                                            { label: 'With estimates', value: formatNumber(mattersCount), color: RV_CHART_COLORS.green },
                                            { label: 'No estimate', value: formatNumber(noEstimate), color: RV_CHART_COLORS.amber, onClick: () => this.handleNoEstimates() },
                                            { label: 'Over estimate', value: formatNumber(exceedingEstimate), color: RV_CHART_COLORS.red },
                                        ]}
                                    />
                                </BarWrapper>
                            );
                        }

                        return (
                            <LoadWrapper className="load-wrapper">
                                <RvLoader size="small" />
                            </LoadWrapper>
                        );
                    }}
                </Query>
            </React.Fragment>
        );
    }

    private handleTotalClick() {
        if (this.props.getSelectedItem) {
            const searchCriterias = new Array<SearchCriteria>();
            searchCriterias.push(
                {property: 'EstimatedCosts', operator: 'gt', comparison: '0'},
                {property: 'ExpectedFutureFees', operator: 'gteq', comparison: '0'},
            );

            this.props.getSelectedItem(
                searchCriterias,
                true,
                'Matter Activity',
                DrillDownType.matterSummaryDrilldown,
                'Matters'
            );
        }        
    }

    private handleNoEstimates() {
        if (this.props.getSelectedItem) {
            const searchCriterias = new Array<SearchCriteria>();

            searchCriterias.push({ property: 'NoEstimatedCosts', operator: 'eq', comparison: '0' });

            this.props.getSelectedItem(searchCriterias, false, `Matter Activity`, DrillDownType.matterSummaryDrilldown, 'Matters');
        }
    }

    private onCloseChart() {
        if (this.props.onCloseChart) {
            return this.props.onCloseChart('isPipelineVsMatterActivityChartOpen');
        }
    }

    // tslint:disable-next-line: no-any
    private handlePopoverOpen(event: any) {
        this.setAnchorEl(event.currentTarget);
    }

    private handlePopoverClose = () => {
        this.setAnchorEl(null);
    }

    // tslint:disable-next-line: no-any
    private setAnchorEl(anchorElValue: any) {
        this.setState({
            anchorEl: anchorElValue,
            open: Boolean(anchorElValue)
        });
    }

    private onPan = (context: { chart: Chart }) => {
        this.chartRef.current.options.scales.y.min = 0;
        this.chartRef.current.update();
    }

    private onHover(event: ChartEvent, elements: ActiveElement[], chart: Chart) {
        (event?.native?.target as HTMLElement).style.cursor = elements.length > 0 ? 'pointer' : 'auto';
    }
}

// tslint:disable-next-line: no-any
export default withStyles(styles as any, { withTheme: true })(PipelineVsMatterActivityChart as any) as any;

const ChartData = gql`
query PipelineVsMatterActivityData($entityType: Int, $entityGuid: String, $responsibility: Int) {
    statistics {
      matter {
        pipelineVsMatterActivity(entityType: $entityType, entityGuid: $entityGuid, responsibility: $responsibility) {
          matterActivity {
            lessThan30
            from31To60
            from61To90
            greaterThan90
          }
          matterPipeline {
            lessThan30Fees
            from31To60Fees
            from61To90Fees
            greaterThan90Fees
          }
          currentMatterEstimateDetails {
            totalFees
            mattersCount
            exceedingEstimate
            noEstimate
            withinEstimate
          }
        }
      }
    }
  }
  `;

const CurrencySymbol = gql`query CurrencySymbol {
    options {currencySymbol {symbol}}
}`;